import { useEffect, useState, useMemo, useRef } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, Phone, Mail, Briefcase, MapPin, FileText, Save, EyeOff, X, Edit, Calendar, DollarSign, Award, CheckCircle, Download, Plus, Trash2, UploadCloud, Loader2 } from 'lucide-react';
import { imageApi } from '@/api/imageApi';
import { Autocomplete, TextField } from '@mui/material';

import { useCreateCandidateProfile, useMyCandidateProfile, useUpdateCandidateProfile, useHideCandidateProfile, useUnhideCandidateProfile, useHiddenFrom } from '@/hooks/useCandidate';
import { useJobs, useJobCategories } from '@/hooks/useJob';
import { useAuthStore } from '@/store/authStore';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { NumberInput } from '@/components/ui/numberInput';
import { Button } from '@/components/ui/button';
import { Spinner, FileUploadDropzone } from '@/components/common';
import AddressSelector from '@/components/common/AddressSelector';

const JOB_TYPES = [
  { value: 'Full-time', label: 'Toàn thời gian' },
  { value: 'Part-time', label: 'Bán thời gian' },
  { value: 'Remote', label: 'Remote' },
  { value: 'Freelance', label: 'Freelance' },
  { value: 'Internship', label: 'Thực tập' },
];

const SUGGESTED_SKILLS = [
  "Quản lý nhà hàng",
  "Pha chế (Bartender)",
  "Pha chế cà phê (Barista)",
  "Phục vụ bàn",
  "Lên thực đơn (Menu planning)",
  "Nấu ăn (Bếp Á)",
  "Nấu ăn (Bếp Âu)",
  "Làm bánh (Bakery/Pastry)",
  "Chăm sóc khách hàng",
  "Quản lý chi phí (Food cost)",
  "Kiểm soát chất lượng",
  "Tuyển dụng & Đào tạo",
  "Tiếng Anh giao tiếp",
  "Marketing F&B",
  "Quản lý kho & Nhập hàng"
];

const SUGGESTED_CERTIFICATIONS = [
  "Chứng chỉ Quản lý Nhà hàng",
  "Chứng chỉ Pha chế",
  "Chứng chỉ Barista Quốc tế",
  "Chứng chỉ Bếp trưởng",
  "Chứng chỉ An toàn Vệ sinh Thực phẩm",
  "Chứng chỉ Tiếng Anh (TOEIC/IELTS)",
  "Chứng chỉ Quản lý chất lượng",
  "Chứng chỉ Sơ cấp cứu",
  "Chứng chỉ Phòng cháy chữa cháy"
];

const schema = z.object({
  fullName: z.string().min(2, 'Nhập họ tên'),
  email: z.string().email('Email không hợp lệ'),
  phoneNumber: z.string().min(1, 'Nhập số điện thoại'),
  desiredPosition: z.string().min(2, 'Nhập vị trí mong muốn'),
  location: z.string().min(2, 'Nhập địa điểm'),
  jobType: z.string().min(1, 'Chọn hình thức làm việc'),
  jobCategoryId: z.string().min(1, 'Chọn ngành nghề'),
  resumeUrl: z.string().url('URL không hợp lệ').optional().or(z.literal('')),
  coverLetter: z.string().optional(),
  experience: z.preprocess((v) => (v === '' || v === undefined ? undefined : Number(String(v).replace(/\D/g, ''))), z.number().min(0).optional()),
  education: z.string().optional(),
  skills: z.array(z.object({ value: z.string() })).optional(),
  expectedSalaryMin: z.preprocess((v) => (v === '' || v === undefined ? undefined : Number(String(v).replace(/\D/g, ''))), z.number().optional()),
  expectedSalaryMax: z.preprocess((v) => (v === '' || v === undefined ? undefined : Number(String(v).replace(/\D/g, ''))), z.number().optional()),
  currencyUnit: z.string().default('VND'),
  timeUnit: z.string().default('month'),
  countryId: z.coerce.number().default(1),
  provinceId: z.coerce.number().optional(),
  districtId: z.coerce.number().optional(),
  wardId: z.coerce.number().optional(),
  experienceDescription: z.string().optional(),
  workHistory: z.array(z.object({
    from: z.string().optional(),
    to: z.string().optional(),
    company: z.string().min(1, 'Nhập tên công ty'),
    position: z.string().min(1, 'Nhập vị trí'),
    description: z.string().optional()
  })).optional(),
  certifications: z.array(z.object({ value: z.string() })).optional(),
  availableFrom: z.string().optional(),
}).refine((data) => {
  if (!data.resumeUrl && !data.coverLetter) {
    return false;
  }
  return true;
}, {
  message: "Vui lòng nhập Thư xin việc nếu không có Link CV",
  path: ["coverLetter"],
});

type FormData = z.infer<typeof schema>;

interface UploadedImg {
  id: string;
  url: string;
  isPrimary: boolean;
  file?: File;
}

export default function CandidateProfilePage() {
  const [isEditing, setIsEditing] = useState(false);
  const { user } = useAuthStore();
  const { data: myProfile, isLoading } = useMyCandidateProfile();
  const { mutate: createProfile, isPending: isCreating } = useCreateCandidateProfile();
  const { mutate: updateProfile, isPending: isUpdating } = useUpdateCandidateProfile();

  const { data: jobCategories = [], isLoading: isLoadingCategories } = useJobCategories();

  const { data: hiddenFromList = [] } = useHiddenFrom(myProfile?.id || '');
  const { mutate: hideProfile, isPending: isHiding } = useHideCandidateProfile();
  const { mutate: unhideProfile, isPending: isUnhiding } = useUnhideCandidateProfile();

  const { data: jobsResponse } = useJobs({} as any);
  const recruiters = useMemo(() => {
    if (!jobsResponse?.data) return [];
    const map = new Map<string, { id: string, name: string }>();
    jobsResponse.data.forEach((job: any) => {
      if (job.creator && job.creator.id) {
        const name = job.company?.name || job.creator.username || job.creator.email || job.creator.id;
        if (!map.has(name)) {
          map.set(name, { id: job.creator.id, name });
        }
      }
    });
    return Array.from(map.values());
  }, [jobsResponse]);

  const [hideRecruiterId, setHideRecruiterId] = useState('');

  const [isOpenToWork, setIsOpenToWork] = useState<boolean>(true);

  const [images, setImages] = useState<UploadedImg[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      currencyUnit: 'VND',
      timeUnit: 'month',
      countryId: 1,
      skills: [],
      workHistory: [],
      certifications: [],
    },
  });

  const { fields: workHistoryFields, append: appendWorkHistory, remove: removeWorkHistory } = useFieldArray({ control, name: 'workHistory' });

  useEffect(() => {
    if (myProfile) {
      setIsEditing(false);
      reset({
        fullName: myProfile.fullName,
        email: myProfile.email,
        phoneNumber: myProfile.phoneNumber,
        desiredPosition: myProfile.desiredPosition ?? '',
        location: myProfile.location ?? '',
        jobType: myProfile.jobType ?? 'Full-time',
        jobCategoryId: myProfile.jobCategoryId ?? '',
        resumeUrl: myProfile.resumeUrl ?? '',
        coverLetter: myProfile.coverLetter ?? '',
        experience: myProfile.experience,
        skills: myProfile.skills?.map((s: string) => ({ value: s })) || [],
        education: myProfile.education ?? '',
        expectedSalaryMin: myProfile.expectedSalaryMin,
        expectedSalaryMax: myProfile.expectedSalaryMax,
        currencyUnit: myProfile.currencyUnit ?? 'VND',
        timeUnit: myProfile.timeUnit ?? 'month',
        provinceId: myProfile.provinceId,
        districtId: myProfile.districtId,
        wardId: myProfile.wardId,
        countryId: myProfile.countryId ?? 1,
        experienceDescription: myProfile.experienceDescription ?? '',
        workHistory: myProfile.workHistory ?? [],
        certifications: myProfile.certifications?.map((c: string) => ({ value: c })) || [],
        availableFrom: myProfile.availableFrom ? new Date(myProfile.availableFrom).toISOString().slice(0, 10) : '',
      });
      setIsOpenToWork(myProfile.isOpenToWork ?? true);
      if (myProfile.images && myProfile.images.length > 0) {
        setImages(myProfile.images.map((img: any) => ({
          id: img.imageId || img.id,
          url: img.image?.publicUrl || '',
          isPrimary: !!img.isPrimary,
        })));
      } else {
        setImages([]);
      }
    } else if (user) {
      setIsEditing(true);
      reset({
        email: user.email ?? '',
        fullName: user.fullName ?? '',
        currencyUnit: 'VND',
        timeUnit: 'month',
        countryId: 1,
        skills: [],
        workHistory: [],
        certifications: [],
      });
      setIsOpenToWork(true);
    }
  }, [myProfile, user, reset]);



  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const validFiles = files.filter(f => f.type.startsWith('image/') && f.size <= 1 * 1024 * 1024);
    if (validFiles.length !== files.length) {
      alert('Một số file bị loại bỏ (không phải ảnh hoặc vượt quá 1MB).');
    }

    const newImgs: UploadedImg[] = validFiles.map((file) => ({
      id: Math.random().toString(36).substring(7),
      url: URL.createObjectURL(file),
      isPrimary: false,
      file,
    }));

    setImages(prev => {
      const next = [...prev, ...newImgs];
      if (next.length > 0 && !next.some(i => i.isPrimary)) next[0].isPrimary = true;
      return next;
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (id: string) => {
    setImages(prev => {
      const next = prev.filter(img => img.id !== id);
      if (next.length > 0 && !next.some(img => img.isPrimary)) {
        next[0].isPrimary = true;
      }
      return next;
    });
  };

  const setPrimaryImage = (id: string) => {
    setImages(prev => prev.map(img => ({ ...img, isPrimary: img.id === id })));
  };

  const onSubmit = async (data: FormData) => {
    try {
      setIsUploading(true);

      const finalImages = [];
      const imageIds = [];
      for (const img of images) {
        if (img.file) {
          const res = await imageApi.upload(img.file);
          if (res?.data?.id) {
            finalImages.push({ id: res.data.id, isPrimary: img.isPrimary });
            imageIds.push(res.data.id);
          }
        } else if (img.id) {
          finalImages.push({ id: img.id, isPrimary: img.isPrimary });
          imageIds.push(img.id);
        }
      }

      const payload = {
        fullName: data.fullName,
        email: data.email,
        phoneNumber: data.phoneNumber || undefined,
        jobCategoryId: data.jobCategoryId,
        jobType: data.jobType,
        currencyUnit: data.currencyUnit,
        timeUnit: data.timeUnit,
        countryId: data.countryId,
        provinceId: data.provinceId,
        districtId: data.districtId,
        wardId: data.wardId,
        isOpenToWork,
        experience: data.experience,
        skills: data.skills?.map(s => s.value) || [],
        education: data.education || undefined,
        expectedSalaryMin: data.expectedSalaryMin,
        expectedSalaryMax: data.expectedSalaryMax,
        desiredPosition: data.desiredPosition,
        location: data.location,
        coverLetter: data.coverLetter || undefined,
        resumeUrl: data.resumeUrl || undefined,
        experienceDescription: data.experienceDescription || undefined,
        workHistory: data.workHistory?.map(w => ({
          company: w.company,
          position: w.position,
          from: w.from || undefined,
          to: w.to || undefined,
          description: w.description || undefined
        })),
        certifications: data.certifications?.map(c => c.value) || [],
        availableFrom: data.availableFrom || undefined,
        imageIds,
        images: finalImages,
      };

      if (myProfile?.id) {
        updateProfile({ id: myProfile.id, data: payload }, {
          onSuccess: () => {
            setIsEditing(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
      } else {
        createProfile(payload as any, {
          onSuccess: () => {
            setIsEditing(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
      }
    } catch (err) {
      console.error(err);
      alert('Có lỗi xảy ra khi tải ảnh hoặc lưu hồ sơ.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleHide = () => {
    if (!hideRecruiterId || !myProfile?.id) return;
    hideProfile({ id: myProfile.id, recruiterId: hideRecruiterId }, {
      onSuccess: () => setHideRecruiterId('')
    });
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="max-w-5xl mx-auto animate-fade-in pb-16 px-4">
      {!isEditing && myProfile ? (
        <CandidateProfileView profile={myProfile} onEdit={() => setIsEditing(true)} />
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between mt-8 mb-6">
            <h1 className="text-2xl font-bold text-gray-900">{myProfile ? 'Chỉnh sửa hồ sơ' : 'Tạo hồ sơ ứng viên'}</h1>
            {myProfile && (
              <Button variant="outline" onClick={() => { reset(); setIsEditing(false); }} className="border-gray-200 text-gray-600">
                Quay lại
              </Button>
            )}
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Thông tin cá nhân */}
            <FormCard title="Thông tin cá nhân">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                <Input label="Họ và tên *" placeholder="Nguyễn Văn A" error={errors.fullName?.message} {...register('fullName')} />
                <Input label="Số điện thoại *" error={errors.phoneNumber?.message} {...register('phoneNumber')} />
                <Input label="Email *" type="email" placeholder="email@example.com" error={errors.email?.message} {...register('email')} className="md:col-span-2" />
              </div>

              <div className="pt-5 border-t border-gray-100 mt-5">
                <Input
                  label="Link CV / Resume"
                  placeholder="https://drive.google.com/..."
                  hint="Google Drive, LinkedIn, Notion..."
                  error={errors.resumeUrl?.message}
                  {...register('resumeUrl')}
                />
                <div className="mt-5">
                  <label className="block text-sm font-bold text-gray-700 mb-2">Thư xin việc</label>
                  <textarea
                    rows={5}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm outline-none focus:border-orange-500 focus:bg-white transition-all shadow-sm hover:border-orange-300"
                    placeholder="Giới thiệu bản thân và kinh nghiệm..."
                    {...register('coverLetter')}
                  />
                  {errors.coverLetter && <p className="text-xs text-red-500 mt-1">{errors.coverLetter.message}</p>}
                </div>
              </div>
            </FormCard>

            {/* Vị trí ứng tuyển */}
            <FormCard title="Vị trí & Ngành nghề">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Input label="Vị trí mong muốn *" placeholder="VD: Head Chef, Store Manager" error={errors.desiredPosition?.message} {...register('desiredPosition')} />
                <Select
                  label="Ngành nghề *"
                  options={jobCategories.map(c => ({ value: c.id, label: c.name }))}
                  placeholder="Chọn ngành nghề"
                  error={errors.jobCategoryId?.message}
                  value={watch('jobCategoryId')}
                  {...register('jobCategoryId')}
                />
                <Select label="Hình thức làm việc *" options={JOB_TYPES} placeholder="Chọn hình thức" error={errors.jobType?.message} value={watch('jobType')} {...register('jobType')} />
              </div>
            </FormCard>

            {/* Địa điểm & Mức lương */}
            <FormCard title="Địa điểm & Mức lương">
              <div className="mb-5">
                <Input label="Nơi làm việc ngắn gọn *" placeholder="Hồ Chí Minh" error={errors.location?.message} {...register('location')} />
              </div>

              <div className="mb-5">
                <label className="block text-[11px] font-bold uppercase tracking-[0.05em] text-gray-500 mb-3">Chi tiết địa chỉ</label>
                <AddressSelector
                  countryId={watch('countryId')}
                  provinceId={watch('provinceId')}
                  districtId={watch('districtId')}
                  wardId={watch('wardId')}
                  onCountryChange={(id) => setValue('countryId', id, { shouldValidate: true })}
                  onProvinceChange={(id) => setValue('provinceId', id, { shouldValidate: true })}
                  onDistrictChange={(id) => setValue('districtId', id, { shouldValidate: true })}
                  onWardChange={(id) => setValue('wardId', id, { shouldValidate: true })}
                  errors={{
                    countryId: errors.countryId?.message,
                    provinceId: errors.provinceId?.message,
                    districtId: errors.districtId?.message,
                    wardId: errors.wardId?.message,
                  }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-5 border-t border-gray-100">
                <NumberInput label="Lương mong muốn (tối thiểu)" placeholder="15.000.000" error={errors.expectedSalaryMin?.message} value={watch('expectedSalaryMin')} {...register('expectedSalaryMin')} />
                <NumberInput label="Lương mong muốn (tối đa)" placeholder="25.000.000" error={errors.expectedSalaryMax?.message} value={watch('expectedSalaryMax')} {...register('expectedSalaryMax')} />
                <Select
                  label="Đơn vị tiền"
                  options={[
                    { value: 'VND', label: 'VND' },
                    { value: 'USD', label: 'USD' },
                  ]}
                  value={watch('currencyUnit')}
                  {...register('currencyUnit')}
                />
                <Select
                  label="Tính theo"
                  options={[
                    { value: 'month', label: 'Tháng' },
                    { value: 'year', label: 'Năm' },
                  ]}
                  value={watch('timeUnit')}
                  {...register('timeUnit')}
                />
              </div>
            </FormCard>

            {/* Kinh nghiệm, Kỹ năng & Học vấn */}
            <FormCard title="Tổng quan Kinh nghiệm, Kỹ năng & Học vấn">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <NumberInput label="Số năm kinh nghiệm" placeholder="3" error={errors.experience?.message} value={watch('experience')} {...register('experience')} />
                <Input label="Học vấn" placeholder="Đại học Bách Khoa TP.HCM" {...register('education')} />
              </div>

              <div className="pt-5 border-t border-gray-100">
                <label className="block text-[11px] font-bold uppercase tracking-[0.05em] text-gray-500 mb-2">
                  Kỹ năng chuyên môn
                </label>
                <Controller
                  name="skills"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      multiple
                      freeSolo
                      options={SUGGESTED_SKILLS}
                      value={field.value?.map((s) => s.value) || []}
                      onChange={(_, data) => field.onChange(data.map((v) => ({ value: v })))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="VD: Java, Spring Boot, MySQL (Nhập và enter để thêm)"
                          error={!!errors.skills}
                          helperText={errors.skills?.message}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                              bgcolor: '#f8fafc',
                              '& fieldset': { borderColor: '#e2e8f0' },
                              '&:hover fieldset': { borderColor: '#f97316' },
                              '&.Mui-focused fieldset': { borderColor: '#f97316' },
                            }
                          }}
                        />
                      )}
                    />
                  )}
                />
              </div>
            </FormCard>

            {/* Kinh nghiệm làm việc chi tiết */}
            <FormCard title="Kinh nghiệm làm việc chi tiết">
              <div className="space-y-6">
                {workHistoryFields.map((field, index) => (
                  <div key={field.id} className="relative p-5 border border-gray-200 rounded-xl bg-gray-50/50">
                    <button type="button" onClick={() => removeWorkHistory(index)} className="absolute top-4 right-4 text-gray-400 hover:text-red-500">
                      <Trash2 size={18} />
                    </button>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 pr-8">
                      <Input label="Công ty *" placeholder="VD: Nhà hàng Sakura" error={errors.workHistory?.[index]?.company?.message} {...register(`workHistory.${index}.company` as const)} />
                      <Input label="Vị trí *" placeholder="VD: Bếp phó" error={errors.workHistory?.[index]?.position?.message} {...register(`workHistory.${index}.position` as const)} />
                      <Input label="Từ (tháng/năm)" type="month" {...register(`workHistory.${index}.from` as const)} />
                      <Input label="Đến (tháng/năm)" type="month" {...register(`workHistory.${index}.to` as const)} />
                    </div>
                    <div className="mt-2">
                      <label className="block text-sm font-bold text-gray-700 mb-2">Mô tả công việc</label>
                      <textarea
                        rows={3}
                        className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-orange-500 transition-all shadow-sm"
                        placeholder="Mô tả công việc bạn đã làm..."
                        {...register(`workHistory.${index}.description` as const)}
                      />
                    </div>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={() => appendWorkHistory({ company: '', position: '', from: '', to: '', description: '' })} className="w-full border-dashed border-2 border-gray-300 text-gray-500 hover:text-orange-500 hover:border-orange-300 hover:bg-orange-50">
                  <Plus size={16} className="mr-2" /> Thêm kinh nghiệm
                </Button>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <label className="block text-sm font-bold text-gray-700 mb-2">Tóm tắt kinh nghiệm chung</label>
                <textarea
                  rows={4}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm outline-none focus:border-orange-500 focus:bg-white transition-all shadow-sm"
                  placeholder="Viết một đoạn ngắn tóm tắt toàn bộ kinh nghiệm của bạn..."
                  {...register('experienceDescription')}
                />
              </div>
            </FormCard>

            {/* Chứng chỉ & Thông tin bổ sung */}
            <FormCard title="Chứng chỉ & Thông tin khác">
              <div className="mb-6">
                <label className="block text-[11px] font-bold uppercase tracking-[0.05em] text-gray-500 mb-2">
                  Chứng chỉ
                </label>
                <Controller
                  name="certifications"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      multiple
                      freeSolo
                      options={SUGGESTED_CERTIFICATIONS}
                      value={field.value?.map((c) => c.value) || []}
                      onChange={(_, data) => field.onChange(data.map((v) => ({ value: v })))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="VD: Chứng chỉ nghề bếp, TOEIC 800... (Nhập và enter để thêm)"
                          error={!!errors.certifications}
                          helperText={errors.certifications?.message}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                              bgcolor: '#f8fafc',
                              '& fieldset': { borderColor: '#e2e8f0' },
                              '&:hover fieldset': { borderColor: '#f97316' },
                              '&.Mui-focused fieldset': { borderColor: '#f97316' },
                            }
                          }}
                        />
                      )}
                    />
                  )}
                />
              </div>

              <div className="pt-6 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-5">
                <Input label="Ngày có thể bắt đầu làm việc" type="date" {...register('availableFrom')} />
              </div>
            </FormCard>

            {/* Upload Ảnh / Portfolio */}
            <FormCard title="Hình ảnh & Năng lực (Portfolio)">
              <div className="space-y-4">
                <FileUploadDropzone
                  onFileSelect={(files) => handleFileUpload({ target: { files } } as any)}
                  isUploading={isUploading}
                  multiple
                  accept="image/*,.pdf"
                  title="Nhấn để chọn ảnh chân dung, bằng cấp hoặc dự án..."
                  subtitle="Hỗ trợ JPG, PNG, PDF. Tối đa 5MB"
                />

                {images.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                    {images.map((img) => (
                      <div key={img.id} className={`relative aspect-video rounded-xl overflow-hidden border-2 group ${img.isPrimary ? "border-orange-500 shadow-md" : "border-gray-200"}`}>
                        <img src={img.url} alt="" className="w-full h-full object-cover" />

                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => removeImage(img.id)}
                            className="w-8 h-8 rounded-full bg-white/20 hover:bg-red-500 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                            title="Xóa ảnh"
                          >
                            <X size={14} />
                          </button>
                        </div>

                        {img.isPrimary ? (
                          <div className="absolute top-2 left-2 px-2 py-1 bg-orange-500 text-white text-[10px] font-bold rounded shadow-sm">
                            Ảnh chính (Avatar)
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setPrimaryImage(img.id)}
                            className="absolute top-2 left-2 px-2 py-1 bg-black/50 hover:bg-black/80 text-white text-[10px] font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            Đặt làm ảnh chính
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </FormCard>

            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-8">
              {myProfile && (
                <Button type="button" variant="outline" onClick={() => { reset(); setIsEditing(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="h-12 px-8 rounded-xl text-base">
                  Hủy
                </Button>
              )}
              <Button type="submit" variant="brand" size="lg" loading={isCreating || isUpdating} className="px-10 h-12 rounded-xl text-base shadow-orange-500/20 shadow-lg">
                <Save size={18} className="mr-2" /> {myProfile ? 'Lưu hồ sơ' : 'Tạo hồ sơ ứng viên'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Bảo mật hồ sơ (Luôn hiển thị ở dưới cùng) */}
      {myProfile && (
        <div className="mt-8">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
            <h2 className="font-bold text-lg text-gray-900 mb-6">Bảo mật hồ sơ</h2>

            <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-gray-50/80 p-4 sm:p-5 rounded-xl border border-gray-100">
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={isOpenToWork}
                  onChange={(e) => {
                    setIsOpenToWork(e.target.checked);
                    if (myProfile?.id) {
                      updateProfile({ id: myProfile.id, data: { isOpenToWork: e.target.checked } });
                    }
                  }}
                />
                <div className="w-12 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <span className="text-[15px] font-bold text-gray-800">Cho phép NTD tìm kiếm</span>
                <span className="hidden sm:block text-gray-300">|</span>
                <p className="text-[14px] text-gray-500">
                  {isOpenToWork ? 'Hồ sơ của bạn đang công khai' : 'Hồ sơ của bạn đang ẩn với NTD'}
                </p>
              </div>
            </div>

            <div className="mt-6">
              <p className="text-sm text-gray-600 mb-4">
                Bạn có thể ẩn hồ sơ của mình đối với một số nhà tuyển dụng cụ thể (họ sẽ không tìm thấy bạn trên hệ thống).
              </p>
              <div className="flex gap-3 max-w-xl">
                <div className="flex-1">
                  <Select
                    value={hideRecruiterId}
                    onChange={(e) => setHideRecruiterId(e.target.value)}
                    placeholder="Chọn nhà tuyển dụng để ẩn..."
                    options={recruiters.map(r => ({ value: r.id, label: r.name }))}
                  />
                </div>
                <Button type="button" onClick={handleHide} loading={isHiding} variant="outline" className="shrink-0 h-[42px] border-gray-200 text-gray-700 hover:bg-gray-50">
                  Ẩn hồ sơ
                </Button>
              </div>

              {hiddenFromList.length > 0 && (
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <h4 className="text-xs font-bold uppercase tracking-wider mb-3 text-gray-500">Đã ẩn với:</h4>
                  <div className="flex flex-wrap gap-2">
                    {hiddenFromList.map((id: string) => {
                      const recName = recruiters.find(r => r.id === id)?.name || id;
                      return (
                        <div key={id} className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg text-sm text-gray-700 border border-gray-200 shadow-sm">
                          <span className="font-medium text-xs truncate max-w-[150px]">{recName}</span>
                          <button
                            type="button"
                            onClick={() => unhideProfile({ id: myProfile.id, recruiterId: id })}
                            disabled={isUnhiding}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const CandidateProfileView = ({ profile, onEdit }: { profile: any, onEdit: () => void }) => {
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="relative mt-8 rounded-2xl bg-white shadow-sm border border-orange-100 overflow-hidden pb-6">
        <div className="h-40 w-full bg-gradient-to-r from-orange-500 to-orange-400"></div>
        <div className="px-6 sm:px-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between -mt-16 sm:-mt-12 mb-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-end gap-5">
              <div className="relative w-32 h-32 rounded-full border-4 border-white bg-orange-50 overflow-hidden shadow-md flex-shrink-0 flex items-center justify-center">
                {profile?.images?.[0]?.image?.publicUrl ? (
                  <img src={profile.images[0].image.publicUrl} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <User size={48} className="text-orange-300" />
                )}
              </div>
              <div className="text-center sm:text-left mt-3 sm:mt-0 pb-1">
                <h1 className="font-bold text-2xl text-gray-900 flex items-center justify-center sm:justify-start gap-2">
                  {profile?.fullName}
                  <CheckCircle size={20} className="text-blue-500" />
                </h1>
                <div className="inline-block mt-2 px-4 py-1 bg-orange-100 text-orange-600 font-semibold rounded-full text-sm">
                  {profile?.desiredPosition || 'Ứng viên'}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6 sm:mt-0 justify-center">
              <Button onClick={onEdit} variant="outline" className="border-orange-200 text-orange-600 hover:bg-orange-50">
                <Edit size={16} className="mr-2" /> Chỉnh sửa
              </Button>
              {/* {profile?.resumeUrl && (
                <Button onClick={() => window.open(profile.resumeUrl, '_blank')} variant="brand" className="shadow-lg shadow-orange-500/20">
                  <Download size={16} className="mr-2" /> Tải CV
                </Button>
              )} */}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-gray-100 pt-6">
            <div className="flex items-center gap-3 text-gray-600">
              <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-orange-500">
                <MapPin size={20} />
              </div>
              <div>
                <p className="text-xs text-gray-400">Địa điểm</p>
                <p className="font-medium">{profile?.location || 'Chưa cập nhật'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-gray-600">
              <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-orange-500">
                <Briefcase size={20} />
              </div>
              <div>
                <p className="text-xs text-gray-400">Kinh nghiệm</p>
                <p className="font-medium">{profile?.experience ? `${profile.experience} năm` : 'Chưa cập nhật'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-gray-600">
              <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center text-orange-500">
                <DollarSign size={20} />
              </div>
              <div>
                <p className="text-xs text-gray-400">Mức lương</p>
                <p className="font-medium">
                  {profile?.expectedSalaryMin ? `${(profile.expectedSalaryMin / 1000000).toString().replace('.', ',')} - ${(profile.expectedSalaryMax / 1000000).toString().replace('.', ',')} triệu / ${profile.timeUnit === 'month' ? 'tháng' : 'năm'}` : 'Thỏa thuận'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <ViewCard title="Thông tin liên hệ" icon={<User size={18} />}>
            <div className="space-y-4 text-sm text-gray-600">
              <div className="flex items-start gap-3">
                <Mail size={16} className="mt-0.5 text-gray-400 shrink-0" />
                <span className="break-all">{profile?.email}</span>
              </div>
              {profile?.phoneNumber && (
                <div className="flex items-start gap-3">
                  <Phone size={16} className="mt-0.5 text-gray-400 shrink-0" />
                  <span>{profile?.phoneNumber}</span>
                </div>
              )}
              <div className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 text-gray-400 shrink-0" />
                <span>{[profile?.ward?.name, profile?.district?.name, profile?.province?.name].filter(Boolean).join(', ') || profile?.location || 'Chưa cập nhật'}</span>
              </div>
            </div>
          </ViewCard>

          {profile?.availableFrom && (
            <ViewCard title="Sẵn sàng làm việc" icon={<Calendar size={18} />}>
              <div className="text-orange-600 font-bold text-lg">
                {new Date(profile.availableFrom).toLocaleDateString('vi-VN')}
              </div>
            </ViewCard>
          )}

          <ViewCard title="Thông tin nghề nghiệp" icon={<Briefcase size={18} />}>
            <div className="space-y-4 text-sm">
              <div>
                <p className="text-gray-400 text-xs mb-1">Vị trí mong muốn</p>
                <p className="font-semibold text-gray-800">{profile?.desiredPosition || 'Chưa cập nhật'}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-1">Loại công việc</p>
                <p className="font-semibold text-gray-800">{profile?.jobType || 'Chưa cập nhật'}</p>
              </div>
              {profile?.education && (
                <div>
                  <p className="text-gray-400 text-xs mb-1">Học vấn</p>
                  <p className="font-semibold text-gray-800">{profile?.education}</p>
                </div>
              )}
            </div>
          </ViewCard>
        </div>

        <div className="lg:col-span-2 space-y-6">
          {profile?.coverLetter && (
            <ViewCard title="Giới thiệu bản thân" icon={<FileText size={18} />}>
              <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap italic">
                "{profile.coverLetter}"
              </p>
            </ViewCard>
          )}

          {profile?.skills && profile.skills.length > 0 && (
            <ViewCard title="Kỹ năng" icon={<Award size={18} />}>
              <div className="flex flex-wrap gap-2">
                {profile.skills.map((skill: string, index: number) => (
                  <span key={index} className="px-3 py-1.5 bg-orange-50 text-orange-600 border border-orange-100 rounded-full text-sm font-medium">
                    {skill}
                  </span>
                ))}
              </div>
            </ViewCard>
          )}

          {profile?.workHistory && profile.workHistory.length > 0 && (
            <ViewCard title="Kinh nghiệm làm việc" icon={<Briefcase size={18} />}>
              <div className="space-y-6">
                {profile.workHistory.map((work: any, index: number) => (
                  <div key={index} className="relative pl-6 border-l-2 border-orange-200 last:border-transparent">
                    <div className="absolute w-3 h-3 bg-orange-500 rounded-full -left-[7.5px] top-1 border-2 border-white"></div>
                    <div className="text-xs text-orange-600 font-bold mb-1">
                      {work.from ? new Date(work.from).toLocaleDateString('vi-VN', { month: '2-digit', year: 'numeric' }) : '?'}
                      {' - '}
                      {work.to ? new Date(work.to).toLocaleDateString('vi-VN', { month: '2-digit', year: 'numeric' }) : 'Hiện tại'}
                    </div>
                    <h4 className="font-bold text-gray-900">{work.position} | {work.company}</h4>
                    {work.description && <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{work.description}</p>}
                  </div>
                ))}
              </div>
            </ViewCard>
          )}

          {profile?.experienceDescription && (
            <ViewCard title="Tóm tắt kinh nghiệm" icon={<FileText size={18} />}>
              <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap">
                {profile.experienceDescription}
              </p>
            </ViewCard>
          )}

          {profile?.certifications && profile.certifications.length > 0 && (
            <ViewCard title="Chứng chỉ" icon={<Award size={18} />}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {profile.certifications.map((cert: string, index: number) => (
                  <div key={index} className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl bg-gray-50/50">
                    <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-orange-500 flex-shrink-0">
                      <Award size={20} />
                    </div>
                    <span className="text-sm font-medium text-gray-800">{cert}</span>
                  </div>
                ))}
              </div>
            </ViewCard>
          )}
        </div>
      </div>
    </div>
  );
};

function FormCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border p-7 space-y-5 bg-white shadow-sm border-gray-100 hover:shadow-md transition-shadow duration-300">
      <h2 className="font-bold text-lg text-gray-900 pb-4 border-b border-gray-100">
        {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function ViewCard({ title, icon, children }: { title: string, icon: React.ReactNode, children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <h3 className="flex items-center gap-2 font-bold text-gray-900 mb-5 pb-3 border-b border-gray-50">
        <span className="text-orange-500">{icon}</span>
        {title}
      </h3>
      {children}
    </div>
  );
}
